"use client";

/* ============================================================
   LANGUAGE PROVIDER

   Letakkan di: app/i18n/LanguageProvider.jsx

   Menyediakan tiga hal ke seluruh komponen lewat React Context:
     lang     - kode bahasa aktif, "id" atau "en"
     setLang  - penggantinya
     t(kunci) - mengambil teks dari kamus

   ============================================================
   KENAPA "use client" DI PALING ATAS

   Context dan useState hanya ada di browser. Tanpa baris ini,
   Next.js App Router memperlakukan berkas ini sebagai Server
   Component dan build gagal.

   Ini TIDAK membuat seluruh situs jadi client-side. Komponen yang
   dibungkusnya tetap boleh Server Component selama mereka tidak
   memanggil useLanguage() sendiri.

   ============================================================
   KENAPA localStorage DIBACA DI DALAM useEffect, BUKAN SAAT RENDER

   Ini bagian paling mudah salah di seluruh berkas ini.

   Next.js merender halaman dua kali: sekali di peladen (HTML awal),
   sekali di browser (hydration). Peladen TIDAK punya localStorage.
   Kalau nilai awal dibaca langsung seperti:

     useState(() => localStorage.getItem("lang"))   // ← JANGAN

   maka peladen menghasilkan "id" sementara browser menghasilkan "en",
   dan React melempar hydration error - seluruh halaman gagal dimuat,
   bukan sekadar bahasanya meleset.

   Karena itu nilai awal SELALU DEFAULT_LOCALE di kedua sisi, lalu
   useEffect menyesuaikannya setelah komponen menempel di browser.

   ⚠️ AKIBAT YANG HARUS KAMU TERIMA: pengunjung yang sudah memilih
   English akan melihat kedipan Bahasa Indonesia sepersekian detik
   setiap kali membuka halaman, sebelum berganti ke Inggris. Ini
   konsekuensi tak terhindarkan dari menyimpan bahasa di browser
   alih-alih di URL. Satu-satunya cara menghilangkannya adalah pindah
   ke routing /en/... - yang memang kamu putuskan untuk tidak dipakai
   sekarang.

   ============================================================
   KALAU KUNCI TIDAK DITEMUKAN

   t() mengembalikan kuncinya sendiri, misalnya "header.nav.homee".
   Ini disengaja. Alternatifnya - mengembalikan string kosong - membuat
   teks hilang diam-diam dan baru ketahuan setelah ada yang mengeluh.
   Kunci yang muncul mentah di layar langsung terlihat saat kamu
   membuka halamannya.

   Sebelum itu, t() mencoba dulu kamus Bahasa Indonesia. Jadi kunci
   yang sudah ada di id tapi belum diterjemahkan ke en akan tampil
   dalam Bahasa Indonesia - jelek, tapi masih terbaca, dan jauh lebih
   baik daripada halaman bolong.
   ============================================================ */

import { createContext, useContext, useEffect, useState } from "react";
import { DICTIONARY, LOCALES, DEFAULT_LOCALE } from "./dictionary";

const STORAGE_KEY = "rsjmc-lang";

const LanguageContext = createContext(null);

function ambil(objek, kunci) {
  let current = objek;

  for (const ruas of kunci.split(".")) {
    if (current == null || typeof current !== "object") return undefined;
    current = current[ruas];
  }

  return typeof current === "string" ? current : undefined;
}

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(DEFAULT_LOCALE);

  /* Baca pilihan yang tersimpan, sekali saja setelah menempel. */
  useEffect(() => {
    try {
      const tersimpan = window.localStorage.getItem(STORAGE_KEY);
      if (tersimpan && LOCALES.includes(tersimpan)) setLangState(tersimpan);
    } catch {
    }
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, lang);
    } catch {
      /* Sama seperti di atas - tidak apa-apa kalau gagal. */
    }

    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = (kode) => {
    if (LOCALES.includes(kode)) setLangState(kode);
  };

  /* t("footer.copyright", { tahun: 2026 })

     PENYISIPAN NILAI, bukan perakitan kalimat. Bedanya penting.

     Merakit kalimat dari dua kunci - t("pre") + tahun + t("post") -
     mengunci urutan katanya ke satu bahasa. Dengan placeholder, tiap
     bahasa menaruh {tahun} di posisi yang alaminya sendiri, dan kalimat
     di kamus tetap utuh sehingga penerjemah bisa membacanya sebagai
     kalimat, bukan sebagai potongan. */
  const t = (kunci, params) => {
    const teks =
      ambil(DICTIONARY[lang], kunci) ??
      ambil(DICTIONARY[DEFAULT_LOCALE], kunci) ??
      kunci;

    if (!params) return teks;

    return teks.replace(/\{(\w+)\}/g, (cocok, nama) =>
      params[nama] !== undefined ? String(params[nama]) : cocok
    );
  };

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);

  /* Pesan error yang menyebut penyebabnya. Tanpa penjagaan ini, yang
     muncul adalah "Cannot destructure property 't' of null" - benar
     secara teknis, tapi tidak memberi tahu bahwa yang kurang adalah
     pembungkus di layout.jsx. */
  if (!ctx) {
    throw new Error(
      "useLanguage() dipakai di luar <LanguageProvider>. " +
        "Pastikan app/layout.jsx sudah membungkus {children} dengan provider ini."
    );
  }

  return ctx;
}
